import {
  CRITICAL_WORKFLOW_PAIN_WINDOW_INSTRUMENTATION_VERSION,
  CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME,
  DEFAULT_CRITICAL_WORKFLOW_PAIN_WINDOW_DURATION_MS,
  MAX_PAIN_OBSERVATION_DURATION_MS,
} from './constants';
import type {
  LoafObservationRuntime,
  LoafPerformanceMetrics,
} from './loafObservationRuntime';
import {
  getInteractionMetrics,
  isEventTimingEntry,
  type RecordedEventTiming,
} from './performanceMetrics';
import type {
  CriticalWorkflowDatadogContext,
  CriticalWorkflowDurationVitalOptions,
  CriticalWorkflowInitiatingEvent,
  CriticalWorkflowObservationOptions,
  RecordCriticalWorkflowDurationVital,
} from './types';

const EVENT_TIMING_DURATION_THRESHOLD_MS = 16;
const MAX_RECENT_EVENT_TIMINGS = 200;
const EVENT_TIMING_OBSERVER_OPTIONS: PerformanceObserverInit & {
  durationThreshold: number;
} = {
  type: 'event',
  buffered: true,
  durationThreshold: EVENT_TIMING_DURATION_THRESHOLD_MS,
};

interface WindowedObservation {
  workflow: string;
  windowDurationMs: number;
  startTime: number;
  endTime: number;
  initiatingEvent?: CriticalWorkflowInitiatingEvent;
  workflowContext?: CriticalWorkflowObservationOptions['workflowContext'];
  recordAbandoned: boolean;
  retriggerCount: number;
  collectorId: number;
  timeoutId: number;
}

export class WindowedPainObserver {
  private eventTimingObserver: PerformanceObserver | undefined;
  private eventTimingSubscriberCount = 0;
  private readonly recentEventTimings: RecordedEventTiming[] = [];
  private readonly observations = new Map<string, WindowedObservation>();

  constructor(
    private readonly runtime: LoafObservationRuntime,
    private readonly recordDurationVital: RecordCriticalWorkflowDurationVital
  ) {}

  initialize(): () => void {
    if (
      !this.runtime.isSupported() ||
      !this.runtime.supportsEntryType('event')
    ) {
      return () => {};
    }

    this.eventTimingSubscriberCount += 1;
    if (this.eventTimingObserver == null) {
      this.eventTimingObserver = new PerformanceObserver(list => {
        this.recordEventTimings(list.getEntries());
      });
      this.eventTimingObserver.observe(EVENT_TIMING_OBSERVER_OPTIONS);
    }

    let stopped = false;
    return () => {
      if (stopped) {
        return;
      }
      stopped = true;
      this.eventTimingSubscriberCount -= 1;
      if (this.eventTimingSubscriberCount === 0) {
        this.eventTimingObserver?.disconnect();
        this.eventTimingObserver = undefined;
        this.recentEventTimings.length = 0;
      }
    };
  }

  observe(options: CriticalWorkflowObservationOptions): boolean {
    const windowDurationMs =
      options.windowDurationMs ??
      DEFAULT_CRITICAL_WORKFLOW_PAIN_WINDOW_DURATION_MS;
    if (
      !this.isValid(options.workflow, windowDurationMs) ||
      !this.runtime.isSupported()
    ) {
      return false;
    }

    const now = performance.now();
    const existing = this.observations.get(options.workflow);
    if (existing != null) {
      if (now < existing.endTime) {
        existing.retriggerCount += 1;
        return true;
      }
      this.completeObservation(options.workflow);
    }

    const started = this.runtime.createCollector(windowDurationMs);
    if (started == null) {
      return false;
    }
    const observation: WindowedObservation = {
      workflow: options.workflow,
      windowDurationMs,
      startTime: started.startTime,
      endTime: started.startTime + windowDurationMs,
      initiatingEvent: options.initiatingEvent,
      workflowContext:
        options.workflowContext == null
          ? undefined
          : {...options.workflowContext},
      recordAbandoned: options.recordAbandoned !== false,
      retriggerCount: 0,
      collectorId: started.collectorId,
      timeoutId: 0,
    };
    observation.timeoutId = window.setTimeout(
      () => this.completeObservation(options.workflow),
      windowDurationMs
    );
    this.observations.set(options.workflow, observation);
    return true;
  }

  handlePageAbandonment(): void {
    if (this.observations.size === 0) {
      return;
    }

    const endTime = performance.now();
    for (const observation of this.observations.values()) {
      this.runtime.truncateCollectorEnd(observation.collectorId, endTime);
    }
    this.runtime.drain();
    this.drainPendingEventTimingEntries();

    for (const observation of this.observations.values()) {
      const duration = Math.min(
        observation.windowDurationMs,
        Math.max(0, endTime - observation.startTime)
      );
      if (!observation.recordAbandoned) {
        this.discardObservation(observation);
        continue;
      }
      const metrics = this.runtime.finalizeCollector(
        observation.collectorId,
        duration
      );
      if (metrics == null) {
        this.discardObservation(observation);
        continue;
      }
      this.finishObservation(observation, {
        startTime: performance.timeOrigin + observation.startTime,
        duration,
        context: this.getContext(observation, metrics, 'abandoned'),
      });
    }
  }

  private isValid(workflow: string, duration: number): boolean {
    return (
      workflow.length > 0 &&
      Number.isFinite(duration) &&
      duration > 0 &&
      duration <= MAX_PAIN_OBSERVATION_DURATION_MS
    );
  }

  private completeObservation(workflow: string): void {
    const observation = this.observations.get(workflow);
    if (observation == null) {
      return;
    }
    this.drainPendingEventTimingEntries();
    const metrics = this.runtime.finalizeCollector(
      observation.collectorId,
      observation.windowDurationMs
    );
    if (metrics == null) {
      this.discardObservation(observation);
      return;
    }
    this.finishObservation(observation, {
      startTime: performance.timeOrigin + observation.startTime,
      duration: observation.windowDurationMs,
      context: this.getContext(observation, metrics, 'completed'),
    });
  }

  private getContext(
    observation: WindowedObservation,
    metrics: LoafPerformanceMetrics,
    outcome: CriticalWorkflowDatadogContext['outcome']
  ): CriticalWorkflowDatadogContext {
    return {
      instrumentationVersion:
        CRITICAL_WORKFLOW_PAIN_WINDOW_INSTRUMENTATION_VERSION,
      workflow: observation.workflow,
      outcome,
      configuredWindowDurationMs: observation.windowDurationMs,
      retriggerCount: observation.retriggerCount,
      ...(observation.workflowContext == null
        ? {}
        : {workflowContext: observation.workflowContext}),
      ...metrics,
      ...getInteractionMetrics(
        this.recentEventTimings,
        observation.initiatingEvent
      ),
    };
  }

  private finishObservation(
    observation: WindowedObservation,
    vital: CriticalWorkflowDurationVitalOptions
  ): void {
    window.clearTimeout(observation.timeoutId);
    this.observations.delete(observation.workflow);
    this.recordDurationVital(CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME, vital);
  }

  private discardObservation(observation: WindowedObservation): void {
    window.clearTimeout(observation.timeoutId);
    this.observations.delete(observation.workflow);
    this.runtime.cancelCollector(observation.collectorId);
  }

  private recordEventTimings(entries: PerformanceEntryList): void {
    for (const entry of entries) {
      if (!isEventTimingEntry(entry)) {
        continue;
      }
      this.recentEventTimings.push({
        name: entry.name,
        startTime: entry.startTime,
        duration: entry.duration,
        processingStart: entry.processingStart,
        processingEnd: entry.processingEnd,
        interactionId: entry.interactionId,
      });
    }
    if (this.recentEventTimings.length > MAX_RECENT_EVENT_TIMINGS) {
      this.recentEventTimings.splice(
        0,
        this.recentEventTimings.length - MAX_RECENT_EVENT_TIMINGS
      );
    }
  }

  private drainPendingEventTimingEntries(): void {
    if (this.eventTimingObserver != null) {
      this.recordEventTimings(this.eventTimingObserver.takeRecords());
    }
  }
}
